import React, { useEffect, useMemo, useState } from "react";
import {
  Download,
  FileText,
  ShieldCheck,
  Check,
  ExternalLink,
  Users,
  TrendingUp,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SITE_DATA } from "../mock";
import { toast } from "sonner";

function formatNumber(n) {
  if (n == null) return "...";
  return n.toLocaleString("en-US");
}

export default function DonutCard() {
  const { mod, versions, changelog, scanner } = SITE_DATA;
  const [selectedVersionId, setSelectedVersionId] = useState(versions[0].id);

  const [activeUsers, setActiveUsers] = useState(0);
  const [playersFetching, setPlayersFetching] = useState(true);
  const [totalDownloads, setTotalDownloads] = useState(null);
  const [bumpKey, setBumpKey] = useState(0);

  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) || versions[0],
    [selectedVersionId, versions]
  );

  async function fetchOnlineUsers() {
    try {
      const res = await fetch("https://copium.best/api/online");
      if (!res.ok) throw new Error();
      const data = await res.json();
      setActiveUsers(data.count ?? 0);
    } catch (_) {
      setActiveUsers(0);
    } finally {
      setPlayersFetching(false);
      setBumpKey((k) => k + 1);
    }
  }

  async function fetchDownloadCount() {
    try {
      const res = await fetch("https://copium.best/api/download");
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTotalDownloads(data.total ?? 1202);
    } catch (_) {
      setTotalDownloads(1202);
    }
  }

  useEffect(() => {
    fetchOnlineUsers();
    fetchDownloadCount();
    const id = setInterval(fetchOnlineUsers, 15000);
    return () => clearInterval(id);
  }, []);

  const handleDownload = async (e) => {
    e.preventDefault();
    const fileName = `copium-${selectedVersion.version}.jar`;

    // 1. Record the download FIRST — before triggering the file,
    //    so mobile browsers that block/navigate on a.click() don't skip the count.
    try {
      const res = await fetch("https://copium.best/api/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ version_id: selectedVersionId }),
      });
      const data = await res.json();
      if (data.total) setTotalDownloads(data.total);
    } catch (_) {}

    // 2. Then trigger the actual file download
    const placeholder = new Blob(
      [
        `Copium Mod — ${selectedVersion.version}\n` +
          `Minecraft: ${selectedVersion.mcVersion}\n` +
          `Released: ${selectedVersion.date}\n\n` +
          `This is a placeholder .jar file for demo purposes.\n`,
      ],
      { type: "application/java-archive" }
    );
    const url = URL.createObjectURL(placeholder);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url);
    }, 2000);

    toast.success(`Downloading ${fileName}`, {
      description: `${selectedVersion.mcVersion} • ${selectedVersion.size}`,
    });
  };

  return (
    <div className="bg-card/60 backdrop-blur-xl border border-border/60 rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
      <div className="p-6 pb-4">
        <div className="flex items-center gap-4">
          <img
            src={mod.iconUrl}
            alt={mod.name}
            className="w-14 h-14 rounded-xl border border-primary/20 object-cover bg-secondary/40"
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground tracking-tight">
              {mod.name}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
              {mod.description}
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <StatPill
            icon={<Users className="w-4 h-4 text-emerald-400" />}
            label="Online players"
            value={playersFetching ? "..." : formatNumber(activeUsers)}
            live={!playersFetching && activeUsers > 0}
            bumpKey={bumpKey}
          />
          <StatPill
            icon={<TrendingUp className="w-4 h-4 text-primary" />}
            label="Total downloads"
            value={formatNumber(totalDownloads)}
            bumpKey={totalDownloads}
          />
        </div>
      </div>

      <div className="px-6 pb-6">
        <Tabs defaultValue="download" className="w-full">
          <TabsList className="grid grid-cols-3 gap-0.5 w-full bg-secondary/50 backdrop-blur rounded-xl p-1 h-auto">
            <TabsTrigger
              value="download"
              className="gap-1.5 text-xs sm:text-sm rounded-lg px-2 sm:px-4 py-2 transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg data-[state=active]:shadow-primary/30"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </TabsTrigger>
            <TabsTrigger
              value="changelog"
              className="gap-1.5 text-xs sm:text-sm rounded-lg px-2 sm:px-4 py-2 transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg data-[state=active]:shadow-primary/30"
            >
              <FileText className="w-3.5 h-3.5" />
              Changelog
            </TabsTrigger>
            <TabsTrigger
              value="scanner"
              className="gap-1.5 text-xs sm:text-sm rounded-lg px-2 sm:px-4 py-2 transition-all data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-lg data-[state=active]:shadow-primary/30"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Scanner
            </TabsTrigger>
          </TabsList>

          <TabsContent value="download" className="mt-5 space-y-4 focus-visible:outline-none">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block uppercase tracking-wider">
                  Version
                </label>
                <Select value={selectedVersionId} onValueChange={setSelectedVersionId}>
                  <SelectTrigger className="bg-secondary/50 border-border/60 rounded-xl h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover/95 backdrop-blur-xl border-border/60">
                    {versions.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={handleDownload}
                className="btn-glint btn-glow inline-flex items-center justify-center whitespace-nowrap text-sm font-semibold gap-2 rounded-xl h-11 px-6 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors hover:scale-[1.02] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground bg-secondary/30 rounded-lg px-4 py-2.5">
              <span>{selectedVersion.size}</span>
              <span className="text-border">•</span>
              <span>{selectedVersion.mcVersion}</span>
              <span className="text-border">•</span>
              <span>{selectedVersion.date}</span>
            </div>
          </TabsContent>

          <TabsContent value="changelog" className="mt-5 space-y-4 focus-visible:outline-none">
            {changelog.map((entry) => (
              <div key={entry.version} className="border-l-2 border-primary/60 pl-4 py-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-foreground tracking-wide">
                    {entry.version}
                  </span>
                  <span className="text-xs text-muted-foreground">{entry.date}</span>
                </div>
                <ul className="space-y-1">
                  {entry.changes.map((c, i) => (
                    <li key={i} className="text-sm text-foreground/90 flex items-start gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="scanner" className="mt-5 space-y-4 focus-visible:outline-none">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-foreground font-medium">
                    <Check className="w-4 h-4 text-emerald-400" />
                    <span>{scanner.title}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Verified by{" "}
                    <a
                      href={scanner.verifiedByUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {scanner.verifiedBy}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[11px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
                  {scanner.detail.enginesClean}/{scanner.detail.enginesTotal} clean
                </div>
              </div>
            </div>
            <VirusTotalCard scan={scanner.detail} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatPill({ icon, label, value, live = false, bumpKey }) {
  return (
    <div className="rounded-xl border border-border/60 bg-secondary/30 px-3.5 py-2.5 flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-background/60 border border-border/60 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            {label}
          </span>
          {live && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" />
              Live
            </span>
          )}
        </div>
        <div
          key={bumpKey}
          className="text-foreground font-semibold text-base leading-tight count-pop tabular-nums"
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function VirusTotalCard({ scan }) {
  return (
    <div className="relative rounded-xl bg-[#1e1f24] border border-[#0f1014] p-3 shadow-lg">
      <div className="absolute -top-1.5 left-8 w-3 h-3 bg-[#1e1f24] border-l border-t border-[#0f1014] rotate-45" />
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 bg-[#0f1014] flex items-center justify-center">
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
            <circle cx="12" cy="12" r="11" fill="#0a1f3d" />
            <path
              d="M12 4l6 2v6c0 4-2.6 6.7-6 8-3.4-1.3-6-4-6-8V6l6-2z"
              fill="#1e6dd8"
            />
            <path
              d="M9 12.5l2 2 4-4.2"
              stroke="white"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="text-white font-semibold text-sm">{scan.botName}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#1e6dd8] text-white rounded px-1.5 py-0.5">
              <Check className="w-2.5 h-2.5" />
              APP
            </span>
            <span className="text-[11px] text-[#949ba4]">{scan.time}</span>
          </div>
          <div className="text-sm text-[#dbdee1] mt-0.5 break-words">
            <span className="font-semibold">{scan.headerText}</span>{" "}
            <span>Verified by</span>{" "}
            <a
              href={scan.madeBy}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#00a8fc] hover:underline break-all"
            >
              {scan.madeBy}
            </a>{" "}
            <span className="text-[#949ba4] text-xs">(edited)</span>
          </div>
          <div className="mt-2 rounded-md bg-[#15161a] border-l-4 border-emerald-500 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-white font-semibold text-sm">{scan.title}</div>
              <div className="text-[11px] text-emerald-400 font-semibold whitespace-nowrap">
                {scan.enginesClean}/{scan.enginesTotal} engines
              </div>
            </div>
            <div className="text-[#dbdee1] text-sm leading-relaxed">{scan.body}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
